import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarDays, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import { apiClient, type AgendaEvent } from '@/lib/api-client';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Retorna as 6 semanas do calendário do mês (sempre 42 células). */
function buildCalendarGrid(month: Date): Date[] {
  const first = startOfMonth(month);
  // semana começa no domingo (locale ptBR usa domingo=0)
  const gridStart = startOfWeek(first, { weekStartsOn: 0 });
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    cells.push(addDays(gridStart, i));
  }
  return cells;
}

/** Mapa de dia ISO (yyyy-MM-dd) → cor do primeiro evento. */
function buildDotMap(events: AgendaEvent[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const ev of events) {
    const day = ev.start.slice(0, 10); // 'yyyy-MM-dd'
    if (!map.has(day)) {
      map.set(day, ev.categoryColor ?? '#10b981');
    }
  }
  return map;
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function CalendarSkeleton() {
  return (
    <div className="flex h-full w-full flex-col gap-1 animate-pulse p-2">
      <div className="mb-2 h-4 w-28 rounded bg-[color-mix(in_oklch,var(--fg)_10%,transparent)]" />
      <div className="grid grid-cols-7 gap-0.5">
        {Array.from({ length: 7 }).map((_, i) => (
          <div
            key={i}
            className="h-4 rounded bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]"
          />
        ))}
        {Array.from({ length: 35 }).map((_, i) => (
          <div
            key={i + 7}
            className="h-7 rounded bg-[color-mix(in_oklch,var(--fg)_6%,transparent)]"
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

const WEEK_LABELS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

export function AgendaMonthCard() {
  const navigate = useNavigate();
  const today = useMemo(() => new Date(), []);
  const [currentMonth, setCurrentMonth] = useState<Date>(() => startOfMonth(today));

  const from = format(
    addDays(startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 0 }), 0),
    "yyyy-MM-dd'T'00:00:00",
  );
  const to = format(
    addDays(
      startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 0 }),
      41,
    ),
    "yyyy-MM-dd'T'23:59:59",
  );

  const { data: events = [], isLoading, isError } = useQuery<AgendaEvent[]>({
    queryKey: ['agenda', 'events', 'month-widget', format(currentMonth, 'yyyy-MM')],
    queryFn: () =>
      apiClient.agendaListEvents({
        from,
        to,
        dateField: 'due',
        status: ['pending', 'overdue', 'completed'],
      }),
    staleTime: 60 * 1000,
  });

  const dotMap = useMemo(() => buildDotMap(events), [events]);
  const cells = useMemo(() => buildCalendarGrid(currentMonth), [currentMonth]);

  const monthLabel = format(currentMonth, 'MMMM yyyy', { locale: ptBR });
  // capitaliza primeira letra
  const monthLabelCap = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);

  function goDay(date: Date) {
    navigate(
      `/escritorio/agenda?date=${format(date, 'yyyy-MM-dd')}&view=day`,
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border shadow-[var(--shadow-card)] backdrop-blur-[var(--blur)] border-[var(--card-border)] bg-[var(--card-bg)] text-[var(--fg)]">
      {/* Header */}
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-[var(--border)] px-3">
        <CalendarDays className="h-4 w-4 text-[var(--fg-muted)]" />
        <h3 className="text-sm font-medium tracking-tight">Calendário</h3>
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={() => setCurrentMonth((m) => subMonths(m, 1))}
            aria-label="Mês anterior"
            className="flex h-6 w-6 items-center justify-center rounded text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)] transition"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <span className="min-w-[7rem] text-center text-xs font-medium text-[var(--fg)]">
            {monthLabelCap}
          </span>
          <button
            type="button"
            onClick={() => setCurrentMonth((m) => addMonths(m, 1))}
            aria-label="Próximo mês"
            className="flex h-6 w-6 items-center justify-center rounded text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)] transition"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col overflow-hidden p-2">
        {isLoading ? (
          <CalendarSkeleton />
        ) : isError ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 text-[var(--fg-muted)]">
            <AlertCircle className="h-6 w-6 opacity-50" />
            <span className="text-xs">Erro ao carregar</span>
          </div>
        ) : (
          <div className="flex h-full flex-col gap-0">
            {/* Labels dos dias da semana */}
            <div className="grid grid-cols-7">
              {WEEK_LABELS.map((label, i) => (
                <div
                  key={i}
                  className="py-0.5 text-center text-[10px] font-medium text-[var(--fg-muted)]"
                >
                  {label}
                </div>
              ))}
            </div>

            {/* Grid de dias — 6 linhas × 7 colunas */}
            <div className="grid flex-1 min-h-0 grid-cols-7 grid-rows-6">
              {cells.map((date, i) => {
                const dayKey = format(date, 'yyyy-MM-dd');
                const isCurrentMonth = isSameMonth(date, currentMonth);
                const isToday = isSameDay(date, today);
                const dotColor = dotMap.get(dayKey);

                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => goDay(date)}
                    aria-label={format(date, "d 'de' MMMM", { locale: ptBR })}
                    className={cn(
                      'min-h-0 flex flex-col items-center justify-center gap-0.5 rounded transition',
                      'hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]',
                      !isCurrentMonth && 'opacity-30',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-5 w-5 items-center justify-center rounded-full text-[11px] leading-none font-medium',
                        isToday
                          ? 'bg-[color-mix(in_oklch,var(--accent)_20%,transparent)] text-[var(--accent)] ring-1 ring-[var(--accent)]'
                          : 'text-[var(--fg)]',
                      )}
                    >
                      {date.getDate()}
                    </span>
                    {/* Pontinho de evento */}
                    <span
                      className="h-1 w-1 rounded-full"
                      style={{
                        backgroundColor: dotColor ?? 'transparent',
                      }}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
